import { MILESTONE_CATEGORIES, type LabeledValue } from '../../constants/elections';

/**
 * Cycle milestones exist for two kinds of cycle — election cycles and legislative
 * cycles (Documentation/ELECTION_OUTCOMES_PRD.md §5.0, "shared code, separate
 * data"). The milestone admin components take a domain; everything that differs
 * between the two lives here, so the components themselves stay one copy.
 */
export type MilestoneDomain = 'election' | 'legislative';

export interface MilestoneDomainConfig {
  /** GET/POST/PUT/DELETE base for the type registry. */
  typesPath: string;
  /** Base for one cycle's milestones. */
  milestonesPath: (cycleId: string) => string;
  /** React Query key prefix for the type registry. */
  typesQueryKey: readonly unknown[];
  /** Query keys to refresh after a type changes (cycle detail and lists). */
  cycleQueryKeys: ReadonlyArray<readonly unknown[]>;
  categories: LabeledValue[];
  /**
   * Whether milestones send citizen reminders. Election milestones do; legislative
   * ones don't yet (#1702), so their Notify controls and reminder fields are hidden
   * rather than shown doing nothing.
   */
  citizenReminders: boolean;
  emptyMilestonesMessage: string;
  deleteMilestoneMessage: (label: string) => string;
  typesIntro: string;
}

export const LEGISLATIVE_MILESTONE_CATEGORIES: LabeledValue[] = [
  { value: 'session', label: 'Session' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'informational', label: 'Informational' },
  { value: 'administrative', label: 'Administrative' },
];

export const MILESTONE_DOMAINS: Record<MilestoneDomain, MilestoneDomainConfig> = {
  election: {
    typesPath: '/election-milestone-types',
    milestonesPath: (cycleId) => `/election-calendars/${cycleId}/milestones`,
    typesQueryKey: ['admin-elections', 'milestone-types'],
    cycleQueryKeys: [['admin-elections', 'cycle'], ['admin-elections', 'cycles']],
    categories: MILESTONE_CATEGORIES,
    citizenReminders: true,
    emptyMilestonesMessage: 'No milestones — every candidate in this cycle shares these dates once configured.',
    deleteMilestoneMessage: (label) => `Delete the "${label}" milestone? Candidate timelines in this cycle stop showing it.`,
    typesIntro: 'The kinds of dates a cycle can have, and how citizens hear about them.',
  },
  legislative: {
    typesPath: '/admin/legislature/milestone-types',
    milestonesPath: (cycleId) => `/admin/legislature/cycles/${cycleId}/milestones`,
    typesQueryKey: ['admin-legislature', 'milestone-types'],
    cycleQueryKeys: [['admin-legislature', 'cycle'], ['admin-legislature', 'cycles']],
    categories: LEGISLATIVE_MILESTONE_CATEGORIES,
    citizenReminders: false,
    emptyMilestonesMessage: 'No milestones yet — add deadlines such as bill introduction or committee report dates.',
    deleteMilestoneMessage: (label) => `Delete the "${label}" milestone from this legislative cycle?`,
    typesIntro: 'The kinds of dates a legislative cycle can have, such as bill introduction deadlines.',
  },
};

export const categoryLabelFor = (domain: MilestoneDomain, category: string | null | undefined): string =>
  MILESTONE_DOMAINS[domain].categories.find((entry) => entry.value === category)?.label ?? (category || '—');
